import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  BarChart3, Download, CheckCircle, Clock, AlertTriangle, Upload,
  Building, Calendar, Users, Globe, RefreshCw, Send, FileText, Settings,
  ChevronDown, Filter, Play, Pause, RotateCcw, Wifi, WifiOff, Key, Eye, EyeOff,
  MapPin, AlertCircle, CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/config';


export default function FlussiTuristiciManager() {
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [regions, setRegions] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  
  // Config Modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configProperty, setConfigProperty] = useState(null);
  const [configForm, setConfigForm] = useState({
    portal_username: '',
    portal_password: '',
    cir: '',
    cin: '',
    istat_code: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  
  // Validation Modal
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Submission state
  const [submitting, setSubmitting] = useState({});

  // Turismatica (Umbria) file generator state
  const [tolmDateRange, setTolmDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [downloadingTolm, setDownloadingTolm] = useState(false);

  // XML Generator state
  const [xmlGeneratorOpen, setXmlGeneratorOpen] = useState(false);
  const [liguriaProvinces, setLiguriaProvinces] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [xmlDateRange, setXmlDateRange] = useState({
    start: new Date().toISOString().split('T')[0].slice(0, 8) + '01',
    end: new Date().toISOString().split('T')[0]
  });
  const [generatingXml, setGeneratingXml] = useState(false);
  const [provinceProperties, setProvinceProperties] = useState([]);
  const [selectedPropertyCode, setSelectedPropertyCode] = useState('');

  // Fetch data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [propsRes, regionsRes, queueRes] = await Promise.all([
        axios.get(`${API}/api/properties`, { headers }),
        axios.get(`${API}/api/flussi-turistici/regions`, { headers }),
        axios.get(`${API}/api/flussi-turistici/queue/status`, { headers })
      ]);

      setProperties(propsRes.data || []);
      setRegions(regionsRes.data);
      setQueueStatus(queueRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyData = async (propertyId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [pendingRes, historyRes, configRes] = await Promise.all([
        axios.get(`${API}/api/flussi-turistici/pending/${propertyId}`, { headers }),
        axios.get(`${API}/api/flussi-turistici/history/${propertyId}?limit=20`, { headers }),
        axios.get(`${API}/api/tourism-flows/config/${propertyId}`, { headers })
      ]);

      setPendingSubmissions(pendingRes.data.pending || []);
      setSubmissionHistory(historyRes.data.history || []);
      
      // Set config form
      const config = configRes.data;
      setConfigForm({
        portal_username: config.portal_username || '',
        portal_password: '',
        cir: config.cir || '',
        cin: config.cin || '',
        istat_code: config.istat_code || ''
      });
    } catch (err) {
      console.error('Error fetching property data:', err);
    }
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    fetchPropertyData(property.id);
  };

  // Fetch Liguria provinces for XML generator
  const fetchLiguriaProvinces = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/tourism/liguria-provinces`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiguriaProvinces(response.data.provinces || []);
    } catch (err) {
      console.error('Error fetching provinces:', err);
    }
  };

  // Fetch properties for selected province
  const fetchProvinceProperties = async (provinceName) => {
    if (!provinceName) {
      setProvinceProperties([]);
      return;
    }
    try {
      const token = localStorage.getItem('hw_token');
      // Find province code
      const province = liguriaProvinces.find(p => p.name === provinceName);
      if (!province) return;
      
      const response = await axios.get(
        `${API}/api/properties`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 1000 }
        }
      );
      
      // Response is an array directly
      const allProperties = Array.isArray(response.data) ? response.data : (response.data.properties || []);
      
      // Filter by province CIR prefix (Liguria properties with matching CIR)
      const filtered = allProperties.filter(p => 
        p.cir && p.cir.startsWith(province.code)
      );
      
      // Sort by name
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      setProvinceProperties(filtered);
    } catch (err) {
      console.error('Error fetching province properties:', err);
    }
  };

  // Generate and download ROSS1000 XML
  const generateRoss1000Xml = async () => {
    if (!selectedProvince) {
      toast.error('Seleziona una provincia');
      return;
    }

    try {
      setGeneratingXml(true);
      const token = localStorage.getItem('hw_token');
      
      const requestBody = {
        province: selectedProvince,
        data_inizio: xmlDateRange.start,
        data_fine: xmlDateRange.end
      };
      
      // Add property_code if a specific property is selected
      if (selectedPropertyCode) {
        requestBody.property_code = selectedPropertyCode;
      }
      
      const response = await axios.post(
        `${API}/api/tourism/generate-ross1000-xml`,
        requestBody,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ROSS1000_${selectedProvince}_${xmlDateRange.start}_${xmlDateRange.end}.xml`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`File XML generato: ${filename}`);
      setXmlGeneratorOpen(false);
    } catch (err) {
      console.error('Error generating XML:', err);
      toast.error(err.response?.data?.detail || 'Errore nella generazione XML');
    } finally {
      setGeneratingXml(false);
    }
  };

  // Genera e scarica file Turismatica (Umbria) — tracciato TOLM ufficiale
  const downloadTurismaticaFile = async () => {
    if (!selectedProperty) return;
    if (!tolmDateRange.start) { toast.error('Seleziona la data di inizio'); return; }
    try {
      setDownloadingTolm(true);
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams({ start_date: tolmDateRange.start });
      if (tolmDateRange.end && tolmDateRange.end !== tolmDateRange.start) params.append('end_date', tolmDateRange.end);
      const response = await axios.get(
        `${API}/api/flussi-turistici/umbria/tolm-file/${selectedProperty.id}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const isZip = (response.headers['content-type'] || '').includes('zip');
      const blob = new Blob([response.data], { type: isZip ? 'application/zip' : 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      let filename = isZip ? 'turismatica.zip' : 'movimento.txt';
      const cd = response.headers['content-disposition'];
      if (cd) { const m = cd.match(/filename="(.+)"/); if (m) filename = m[1]; }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`File Turismatica generato: ${filename}`);
    } catch (err) {
      let msg = 'Errore nella generazione del file';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch (e) { /* noop */ }
      } else if (err.response?.data?.detail) msg = err.response.data.detail;
      toast.error(msg);
    } finally {
      setDownloadingTolm(false);
    }
  };

  // Submit booking to portal
  const submitBooking = async (bookingId) => {
    try {
      setSubmitting(prev => ({ ...prev, [bookingId]: true }));
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.post(
        `${API}/api/flussi-turistici/submit/booking/${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Dati inviati con successo!');
        fetchPropertyData(selectedProperty.id);
      } else {
        toast.error(response.data.message || 'Errore nell\'invio');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSubmitting(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  // Validate booking before submission
  const validateBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/flussi-turistici/validate/booking/${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValidationResult(response.data);
      setShowValidationModal(true);
    } catch (err) {
      toast.error('Errore nella validazione');
    }
  };

  // Add to queue
  const addToQueue = async (bookingId) => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/flussi-turistici/queue/add`,
        {
          property_id: selectedProperty.id,
          submission_type: 'booking',
          booking_id: bookingId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Aggiunto alla coda di invio');
      fetchAllData();
    } catch (err) {
      toast.error('Errore');
    }
  };

  // Test connection
  const testConnection = async () => {
    if (!configProperty) return;
    
    try {
      setTestingConnection(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/flussi-turistici/test-connection/${configProperty.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Connessione OK al portale ${response.data.portal}`);
      } else {
        toast.error(response.data.error || 'Connessione fallita');
      }
    } catch (err) {
      toast.error('Errore nel test connessione');
    } finally {
      setTestingConnection(false);
    }
  };

  // Save credentials
  const saveCredentials = async () => {
    if (!configProperty) return;
    
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/flussi-turistici/config/${configProperty.id}/credentials`,
        configForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Credenziali salvate!');
      setShowConfigModal(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  // Retry failed item
  const retryFailedItem = async (itemId) => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/flussi-turistici/queue/retry/${itemId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Elemento rimesso in coda');
      fetchAllData();
    } catch (err) {
      toast.error('Errore');
    }
  };

  // Fetch failed items
  const fetchFailedItems = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/flussi-turistici/queue/failed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFailedItems(response.data.failed_items || []);
    } catch (err) {
      console.error('Error fetching failed items:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'queue') {
      fetchFailedItems();
    }
  }, [activeTab]);

  // Get region badge color
  const getRegionBadge = (region) => {
    if (!regions) return null;
    const info = regions.regions?.[region];
    if (!info) return <Badge variant="outline">Sconosciuta</Badge>;
    
    return info.implemented ? (
      <Badge className="bg-green-100 text-green-800">{info.portal_name}</Badge>
    ) : (
      <Badge className="bg-orange-100 text-orange-800">{info.portal_name} (da implementare)</Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="flussi-turistici-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flussi Turistici - Invio Automatico</h1>
          <p className="text-gray-600">Gestione invio automatico dati ai portali regionali</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              fetchLiguriaProvinces();
              setXmlGeneratorOpen(true);
            }} 
            variant="outline"
            className="border-green-500 text-green-700 hover:bg-green-50"
            data-testid="generate-xml-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Genera XML Liguria
          </Button>
          <Button onClick={fetchAllData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* XML Generator Dialog */}
      <Dialog open={xmlGeneratorOpen} onOpenChange={setXmlGeneratorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Genera XML ROSS1000 - Liguria
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Genera il file XML per l'upload manuale sui portali ROSS1000 della Liguria.
              Seleziona la provincia e il periodo per cui generare i dati.
            </p>

            {/* Province Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Provincia</label>
              <select
                value={selectedProvince}
                onChange={(e) => {
                  setSelectedProvince(e.target.value);
                  setSelectedPropertyCode('');
                  fetchProvinceProperties(e.target.value);
                }}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500"
                data-testid="province-select"
              >
                <option value="">-- Seleziona Provincia --</option>
                {liguriaProvinces.map((prov) => (
                  <option key={prov.code} value={prov.name}>
                    {prov.name} ({prov.properties_count} immobili)
                  </option>
                ))}
              </select>
            </div>

            {/* Property Selection (optional) */}
            {selectedProvince && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Immobile <span className="text-gray-400 font-normal">(opzionale - lascia vuoto per tutta la provincia)</span>
                </label>
                <select
                  value={selectedPropertyCode}
                  onChange={(e) => setSelectedPropertyCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500"
                  data-testid="property-select"
                >
                  <option value="">-- Tutti gli immobili della provincia --</option>
                  {provinceProperties.map((prop) => (
                    <option key={prop.property_code} value={prop.property_code}>
                      {prop.property_code} - {prop.name} ({prop.cir})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Inizio</label>
                <Input
                  type="date"
                  value={xmlDateRange.start}
                  onChange={(e) => setXmlDateRange(prev => ({ ...prev, start: e.target.value }))}
                  data-testid="date-start"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Fine</label>
                <Input
                  type="date"
                  value={xmlDateRange.end}
                  onChange={(e) => setXmlDateRange(prev => ({ ...prev, end: e.target.value }))}
                  data-testid="date-end"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Nota:</strong> {selectedPropertyCode 
                  ? `Il file includerà solo l'immobile selezionato (${selectedPropertyCode}) con i relativi arrivi, partenze e chiusure.`
                  : 'Il file includerà tutti gli immobili della provincia selezionata con i relativi arrivi, partenze e chiusure nel periodo indicato.'
                }
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setXmlGeneratorOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={generateRoss1000Xml}
              disabled={generatingXml || !selectedProvince}
              className="bg-green-600 hover:bg-green-700"
              data-testid="download-xml-btn"
            >
              {generatingXml ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generazione...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Scarica XML
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      {queueStatus && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">In Coda</p>
                <p className="text-xl font-bold">{queueStatus.pending}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Loader2 className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">In Elaborazione</p>
                <p className="text-xl font-bold">{queueStatus.processing}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <RotateCcw className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Retry Programmato</p>
                <p className="text-xl font-bold">{queueStatus.retry_scheduled}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50/30">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Completati</p>
                <p className="text-xl font-bold text-green-700">{queueStatus.success}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={queueStatus.max_retries_exceeded > 0 ? "border-red-200 bg-red-50/30" : ""}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Falliti</p>
                <p className="text-xl font-bold text-red-700">{queueStatus.max_retries_exceeded}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard">
            <Building className="h-4 w-4 mr-2" />
            Proprietà
          </TabsTrigger>
          <TabsTrigger value="pending">
            <Upload className="h-4 w-4 mr-2" />
            Da Inviare
          </TabsTrigger>
          <TabsTrigger value="queue">
            <Clock className="h-4 w-4 mr-2" />
            Coda
          </TabsTrigger>
          <TabsTrigger value="regions">
            <MapPin className="h-4 w-4 mr-2" />
            Regioni
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab - Properties */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Properties List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Proprietà</CardTitle>
                  <div className="relative mt-2">
                    <Input
                      type="text"
                      placeholder="Cerca per nome, città, regione o CIR..."
                      value={propertyFilter}
                      onChange={(e) => setPropertyFilter(e.target.value)}
                      className="pr-8"
                      data-testid="property-search-input"
                    />
                    {propertyFilter && (
                      <button
                        onClick={() => setPropertyFilter('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        data-testid="property-search-clear"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {(() => {
                      const q = propertyFilter.trim().toLowerCase();
                      const filtered = !q ? properties : properties.filter(p =>
                        (p.name || '').toLowerCase().includes(q) ||
                        (p.city || '').toLowerCase().includes(q) ||
                        (p.region || '').toLowerCase().includes(q) ||
                        (p.cir || '').toLowerCase().includes(q) ||
                        (p.cin || '').toLowerCase().includes(q)
                      );
                      if (filtered.length === 0) {
                        return <p className="text-sm text-gray-400 text-center py-6">Nessun immobile trovato per "{propertyFilter}"</p>;
                      }
                      return filtered.map((property) => (
                      <div
                        key={property.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-blue-300 ${
                          selectedProperty?.id === property.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                        onClick={() => handlePropertySelect(property)}
                        data-testid={`property-row-${property.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{property.name}</span>
                              {getRegionBadge(property.region)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {property.city}, {property.region}
                            </div>
                            <div className="flex gap-2 mt-1">
                              {property.cir && (
                                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                  CIR: {property.cir}
                                </span>
                              )}
                              {property.cin && (
                                <span className="text-xs bg-blue-100 px-2 py-0.5 rounded">
                                  CIN: {property.cin}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfigProperty(property);
                              setShowConfigModal(true);
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      ));
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Property Details */}
            <div>
              {selectedProperty ? (
                <Card className="sticky top-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      {selectedProperty.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Turismatica (Umbria): generazione file TOLM per upload manuale */}
                    {selectedProperty.region === 'Umbria' && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2" data-testid="turismatica-panel">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-emerald-700" />
                          <h3 className="font-medium text-emerald-900">File Turismatica (Umbria)</h3>
                        </div>
                        <p className="text-xs text-emerald-800">
                          Genera i file movimento ospiti nel tracciato ufficiale TOLM.
                          Caricali su <a href="https://turismatica.regione.umbria.it" target="_blank" rel="noreferrer" className="underline font-medium">turismatica.regione.umbria.it</a> (login SPID)
                          in <b>Movimento ospiti → Carica da file</b>, un giorno alla volta in ordine cronologico.
                          I giorni senza movimenti sono esclusi dallo ZIP: per quelli usa il pulsante <b>"Nessun nuovo movimento"</b> sul portale (vedi LEGGIMI.txt incluso).
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-emerald-900">Dal giorno</label>
                            <Input type="date" value={tolmDateRange.start}
                              onChange={(e) => setTolmDateRange(prev => ({ ...prev, start: e.target.value }))}
                              className="h-8 text-xs" data-testid="tolm-start-date" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-emerald-900">Al giorno</label>
                            <Input type="date" value={tolmDateRange.end}
                              onChange={(e) => setTolmDateRange(prev => ({ ...prev, end: e.target.value }))}
                              className="h-8 text-xs" data-testid="tolm-end-date" />
                          </div>
                        </div>
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={downloadingTolm} onClick={downloadTurismaticaFile} data-testid="tolm-download-btn">
                          {downloadingTolm ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generazione...</>
                          ) : (
                            <><Download className="h-3.5 w-3.5 mr-1.5" /> Scarica file movimento (1 giorno = .txt, più giorni = .zip)</>
                          )}
                        </Button>
                      </div>
                    )}
                    {/* Pending submissions for this property */}
                    <div>
                      <h3 className="font-medium mb-2">Da Inviare ({pendingSubmissions.length})</h3>
                      {pendingSubmissions.length > 0 ? (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {pendingSubmissions.map((item) => (
                            <div key={item.id} className="p-2 bg-gray-50 rounded border text-sm">
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className="font-medium">{item.guest_name}</span>
                                  <div className="text-xs text-gray-500">
                                    {item.check_in?.split('T')[0]} - {item.check_out?.split('T')[0]}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => validateBooking(item.id)}
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    disabled={submitting[item.id]}
                                    onClick={() => submitBooking(item.id)}
                                  >
                                    {submitting[item.id] ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Send className="h-3 w-3" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Nessuna prenotazione da inviare</p>
                      )}
                    </div>

                    {/* Recent history */}
                    <div>
                      <h3 className="font-medium mb-2">Ultimi Invii</h3>
                      {submissionHistory.length > 0 ? (
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {submissionHistory.slice(0, 5).map((item, i) => (
                            <div key={i} className="p-2 bg-gray-50 rounded border text-xs">
                              <div className="flex justify-between">
                                <span>{item.submitted_at?.split('T')[0]}</span>
                                <Badge className={
                                  item.status === 'success' ? 'bg-green-100 text-green-800' :
                                  item.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }>
                                  {item.status}
                                </Badge>
                              </div>
                              <p className="text-gray-600 mt-1 truncate">{item.message}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Nessun invio recente</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-4 border-t">
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => {
                          setConfigProperty(selectedProperty);
                          setShowConfigModal(true);
                        }}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Configura Credenziali
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>Seleziona una proprietà</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Prenotazioni da Inviare</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingSubmissions.length > 0 ? (
                <div className="space-y-2">
                  {pendingSubmissions.map((item) => (
                    <div key={item.id} className="p-4 border rounded-lg flex justify-between items-center">
                      <div>
                        <div className="font-medium">{item.guest_name}</div>
                        <div className="text-sm text-gray-500">
                          {item.check_in?.split('T')[0]} - {item.check_out?.split('T')[0]}
                        </div>
                        <div className="text-xs text-gray-400">
                          {item.guests_documented} ospiti documentati
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => validateBooking(item.id)}>
                          Valida
                        </Button>
                        <Button variant="outline" onClick={() => addToQueue(item.id)}>
                          <Clock className="h-4 w-4 mr-2" />
                          In Coda
                        </Button>
                        <Button 
                          disabled={submitting[item.id]}
                          onClick={() => submitBooking(item.id)}
                        >
                          {submitting[item.id] ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Invia Ora
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-300" />
                  <p>Tutte le prenotazioni sono state inviate!</p>
                  <p className="text-sm">Seleziona una proprietà per vedere i pending</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Queue Tab */}
        <TabsContent value="queue">
          <Card>
            <CardHeader>
              <CardTitle>Coda Invii e Elementi Falliti</CardTitle>
            </CardHeader>
            <CardContent>
              {failedItems.length > 0 ? (
                <div className="space-y-2">
                  {failedItems.map((item) => (
                    <div key={item.id} className="p-4 border border-red-200 bg-red-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="font-medium">Booking: {item.booking_id}</span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Proprietà: {item.property_id}
                          </div>
                          <div className="text-sm text-red-600 mt-1">
                            Errore: {item.last_error}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Tentativi: {item.attempts} / {item.max_attempts}
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => retryFailedItem(item.id)}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Riprova
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-300" />
                  <p>Nessun elemento fallito!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regions Tab */}
        <TabsContent value="regions">
          {regions && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Implemented Regions */}
              <Card className="border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    Regioni Implementate ({regions.implemented_count})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {regions.implemented_regions?.map((region) => {
                      const info = regions.regions[region];
                      const clientType = info.client_type || 'soap';
                      const clientBadge = {
                        'soap': { label: 'API SOAP', color: 'bg-blue-100 text-blue-800' },
                        'file_upload': { label: 'File Upload', color: 'bg-purple-100 text-purple-800' },
                        'api_token': { label: 'API Token', color: 'bg-cyan-100 text-cyan-800' },
                        'oauth2': { label: 'OAuth2', color: 'bg-indigo-100 text-indigo-800' },
                        'mixed': { label: 'Misto', color: 'bg-gray-100 text-gray-800' }
                      }[clientType] || { label: clientType, color: 'bg-gray-100 text-gray-800' };
                      
                      return (
                        <div key={region} className="p-3 bg-green-50 rounded border border-green-200">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{region}</span>
                              {info.local_name && info.local_name !== info.portal_name && (
                                <span className="text-sm text-gray-500 ml-2">({info.local_name})</span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Badge className={clientBadge.color}>{clientBadge.label}</Badge>
                              <Badge className="bg-green-100 text-green-800">{info.portal_name}</Badge>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{info.notes}</p>
                          {info.requires_accreditation && (
                            <p className="text-xs text-orange-600 mt-1">⚠️ Richiede accreditamento</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Regions */}
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <AlertTriangle className="h-5 w-5" />
                    Regioni da Implementare ({regions.pending_count})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {regions.pending_regions?.length > 0 ? (
                      regions.pending_regions.map((region) => {
                        const info = regions.regions[region];
                        return (
                          <div key={region} className="p-3 bg-orange-50 rounded border border-orange-200">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{region}</span>
                              <Badge className="bg-orange-100 text-orange-800">{info.portal_name}</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{info.notes}</p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-green-600">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                        <p>Tutte le regioni sono supportate!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Configura Credenziali Portale
            </DialogTitle>
          </DialogHeader>
          
          {configProperty && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{configProperty.name}</p>
                <p className="text-sm text-gray-500">{configProperty.region}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Username Portale *</label>
                  <Input
                    value={configForm.portal_username}
                    onChange={(e) => setConfigForm({...configForm, portal_username: e.target.value})}
                    placeholder="Username portale regionale"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Password Portale *</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={configForm.portal_password}
                      onChange={(e) => setConfigForm({...configForm, portal_password: e.target.value})}
                      placeholder="Password portale regionale"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-orange-600 mt-1">
                    Nota: Le password dei portali regionali scadono ogni 90 giorni
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium">CIR (Codice Identificativo Regionale) *</label>
                  <Input
                    value={configForm.cir}
                    onChange={(e) => setConfigForm({...configForm, cir: e.target.value})}
                    placeholder="es. 012345-TUR-00001"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">CIN (Codice Identificativo Nazionale) *</label>
                  <Input
                    value={configForm.cin}
                    onChange={(e) => setConfigForm({...configForm, cin: e.target.value})}
                    placeholder="es. IT012345ABC123"
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    Il CIN è obbligatorio dal 2026
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium">Codice ISTAT</label>
                  <Input
                    value={configForm.istat_code}
                    onChange={(e) => setConfigForm({...configForm, istat_code: e.target.value})}
                    placeholder="Codice ISTAT struttura (opzionale)"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={testConnection}
              disabled={testingConnection}
            >
              {testingConnection ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wifi className="h-4 w-4 mr-2" />
              )}
              Test Connessione
            </Button>
            <Button onClick={saveCredentials}>
              Salva Credenziali
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Modal */}
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Risultato Validazione</DialogTitle>
          </DialogHeader>
          
          {validationResult && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {validationResult.valid ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {validationResult.valid ? 'Dati Validi - Pronto per invio' : 'Dati Non Validi'}
                  </span>
                </div>
              </div>

              {validationResult.errors?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-2">Errori ({validationResult.errors_count})</h4>
                  <div className="space-y-1">
                    {validationResult.errors.map((err, i) => (
                      <div key={i} className="text-sm p-2 bg-red-50 rounded">
                        <span className="font-medium">{err.field}:</span> {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {validationResult.warnings?.length > 0 && (
                <div>
                  <h4 className="font-medium text-orange-700 mb-2">Avvisi ({validationResult.warnings_count})</h4>
                  <div className="space-y-1">
                    {validationResult.warnings.map((warn, i) => (
                      <div key={i} className="text-sm p-2 bg-orange-50 rounded">
                        <span className="font-medium">{warn.field}:</span> {warn.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationModal(false)}>
              Chiudi
            </Button>
            {validationResult?.valid && (
              <Button onClick={() => {
                submitBooking(validationResult.booking_id);
                setShowValidationModal(false);
              }}>
                <Send className="h-4 w-4 mr-2" />
                Invia Ora
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
